import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Stack } from 'react-bootstrap';
import { Delete, Edit, Publish, Refresh } from '@mui/icons-material';
import { Typography } from '@mui/material';
import {
  deleteReleaseNote,
  fetchReleaseNotes,
  publishReleaseNote,
} from '../../modules/admin';
import TableComponent from '../elements/TableComponent';
import { ReleaseNoteTableHeaders } from '../project-dashboard/test-case/testCaseTableHead';
import CreateReleaseNoteModal from './CreateReleaseNoteModal';
import TableActionsMenu from '../elements/TableActionsMenu';
import { useNotification } from '../../hooks/NotificationContext';

const AdminReleaseNotesListPage = () => {
  const [loading, setLoading] = useState(true);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const { addNotification } = useNotification();
  const [mode, setMode] = useState('add');
  const [order, setOrder] = useState('desc');

  const fetchReleaseNotesList = async () => {
    setLoading(true);
    try {
      const response = await fetchReleaseNotes(page, perPage);
      setReleaseNotes(response?.release_notes || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      console.error('Error fetching release notes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReleaseNotesList();
  }, [page]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEdit = (releaseNote) => {
    setMode('edit');
    setFormData(releaseNote);
    setShowCreateModal(true);
  };

  const handleDelete = async (releaseNoteId) => {
    setLoading(true);
    try {
      await deleteReleaseNote(releaseNoteId);
      fetchReleaseNotesList();
      addNotification('success', 'Release note deleted successfully!');
    } catch (error) {
      addNotification('danger', 'Failed to delete release note.');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (releaseNoteId) => {
    setLoading(true);
    try {
      await publishReleaseNote(releaseNoteId);
      fetchReleaseNotesList();
      addNotification('success', 'Release note published successfully!');
    } catch (error) {
      addNotification('danger', 'Failed to publish release note.');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData(null);
    setMode('add');
    setShowCreateModal(false);
  };

  return (
    <>
      <Row>
        <Col>
          <Typography variant="h4" className="mb-3">
            Release Notes
          </Typography>
        </Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col className="d-flex justify-content-end">
          <Stack direction="horizontal" gap={2}>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              + Create Release Note
            </Button>

            <Button
              variant="outline-secondary"
              className="remove-focus"
              onClick={fetchReleaseNotesList}
              title="Click to refresh the list">
              <Refresh color={'#6c757d'} size={'20px'} />
            </Button>
          </Stack>
        </Col>
      </Row>
      <Row>
        <Col>
          <TableComponent
            data={releaseNotes}
            headerTabs={ReleaseNoteTableHeaders}
            page={page}
            handlePageChange={handlePageChange}
            perPage={perPage}
            order={order}
            setOrder={setOrder}
            isLoading={loading}
            striped
            totalCount={totalCount}
            showPages
            tablekey="release notes"
            showCount={true}
            action={(row) => {
              const options = [
                {
                  label: 'Edit',
                  icon: <Edit fontSize="small" />,
                  onClick: () => handleEdit(row),
                },
              ];
              if (row.published === false) {
                options.push(
                  {
                    label: 'Publish',
                    icon: <Publish fontSize="small" />,
                    onClick: () => handlePublish(row.id),
                    disabled: row?.published,
                  },
                  {
                    label: 'Delete',
                    icon: <Delete fontSize="small" />,
                    onClick: () => handleDelete(row.id),
                  }
                );
              }

              return <TableActionsMenu options={options} />;
            }}
          />
        </Col>
      </Row>
      <CreateReleaseNoteModal
        showModal={showCreateModal}
        handleClose={handleModalClose}
        refreshList={fetchReleaseNotesList}
        initialData={formData}
        mode={mode}
      />
    </>
  );
};

export default AdminReleaseNotesListPage;
